import type { EntityName, OutboxOp, SyncColumns } from '@cadencia/contracts';
import { resolveConflict } from '@cadencia/domain';
import type { CadenciaDb } from '../db.js';
import { logger } from '../../lib/logger.js';
import { keysToCamel, normalizarSellos } from './case.js';
import { ENTITY_TO_TABLE, type SyncOutcome, type SyncTransport } from './types.js';

const PUSH_BATCH = 100;
const PULL_PAGE = 500;
const MAX_ATTEMPTS = 5;

/**
 * Orden de envio, por dependencia de claves ajenas: cada entidad va despues de
 * todas aquellas a las que apunta.
 *
 * Por que hace falta: el servidor aplica las operaciones EN EL ORDEN RECIBIDO.
 * Un hijo enviado antes que su padre viola la clave ajena, y el outbox NO
 * conserva el orden de insercion —`where('status')` recorre el indice y
 * devuelve las filas por clave primaria, que es un UUID aleatorio, y ordenar
 * por `createdAt` no lo corrige porque una sola transaccion escribe decenas de
 * operaciones dentro del mismo milisegundo—. El resultado era un orden
 * efectivamente aleatorio, que fallaba unas veces si y otras no.
 *
 * `orden-push.test.ts` comprueba esta lista contra las claves ajenas
 * declaradas en las migraciones: si se añade una entidad o una referencia
 * nueva y esta lista no se actualiza, el test falla.
 */
const ORDEN_DE_ENVIO: readonly EntityName[] = [
  // Sin dependencias.
  'profiles',
  'user_preferences',
  'gyms',
  'training_blocks',
  'readiness_checkins',
  // Solo apunta a `auth.users`: no depende de ninguna tabla de `public`.
  'training_decisions',
  // Dependen de las anteriores.
  'equipment',
  'training_weeks',
  'available_loads',
  'progression_state',
  'training_sessions',
  'session_exercises',
  'workout_logs',
  'set_prescriptions',
  'warmup_prescriptions',
  'load_recommendations',
  'performed_sets',
  'rest_events',
  // El cardio va al final y en este orden: `cardio_logs` apunta a
  // `cardio_prescriptions`, y ademas la prescripcion puede apuntar a una sesion
  // de fuerza. Las dos tienen que haber viajado ya.
  'cardio_prescriptions',
  'cardio_logs',
  // Nutricion. `planned_meals` apunta a `meal_plans` y `meal_logs` a
  // `planned_meals`: el orden entre los tres no es negociable.
  'daily_weights',
  'user_recipe_settings',
  'nutrition_targets',
  'meal_plans',
  'planned_meals',
  'meal_logs',
  // Compra. `shopping_list_items` apunta a `shopping_lists`, y la lista puede
  // apuntar a un `meal_plans`: los tres tienen que ir despues del menu.
  'pantry_items',
  'shopping_lists',
  'shopping_list_items',
  // Progreso. Ninguna de las dos apunta a otra tabla de `public`, asi que su
  // sitio aqui es indiferente; van al final por orden de fase.
  'body_measurements',
  'weekly_checkins',
  // Pasos (§17): tampoco apunta a nada.
  'daily_activity',
  // Evidencia. La busqueda ANTES que sus fichas: `evidence_sources.search_id`
  // la referencia, y una ficha que suba primero encuentra la clave ajena vacia.
  'evidence_searches',
  'evidence_sources',
  // IA. `ai_requests` y `ai_usage_budget` aparecen aqui por completitud del
  // registro —el test de orden compara esta lista con las entidades
  // declaradas—, pero el cliente NUNCA las encola: las escribe la Edge
  // Function y aqui solo bajan. Lo impide el tipo `LocalTable` de
  // `writes.ts`, que no las incluye. `ai_decisions` si se empuja, y va
  // despues de `ai_requests` porque la referencia.
  'ai_requests',
  'ai_usage_budget',
  'ai_decisions',
];

const RANGO: ReadonlyMap<EntityName, number> = new Map(
  ORDEN_DE_ENVIO.map((entidad, indice) => [entidad, indice]),
);

/**
 * Comparador total y determinista: primero la dependencia, luego el tiempo y,
 * en caso de empate, el identificador. Sin el ultimo desempate el orden seguiria
 * dependiendo de como devuelva las filas el motor de la base local.
 */
function compararOps(a: OutboxOp, b: OutboxOp): number {
  // Una entidad desconocida se envia la ultima: no puede ser padre de nada
  // conocido, y asi un contrato mas nuevo que este motor no rompe el lote.
  const rangoA = RANGO.get(a.entity) ?? ORDEN_DE_ENVIO.length;
  const rangoB = RANGO.get(b.entity) ?? ORDEN_DE_ENVIO.length;
  if (rangoA !== rangoB) return rangoA - rangoB;
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;
  if (a.opId === b.opId) return 0;
  return a.opId < b.opId ? -1 : 1;
}

/** Expuesto solo para las pruebas: el orden real que se envia al servidor. */
export function ordenarParaEnvio(ops: readonly OutboxOp[]): OutboxOp[] {
  return [...ops].sort(compararOps);
}

export interface SyncOptions {
  db: CadenciaDb;
  transport: SyncTransport;
  /** Se invoca cuando una fila remota pisa una local: la UI lo muestra. */
  onConflict?: (entity: EntityName, id: string, reason: string) => void;
}

/**
 * Un ciclo completo: PUSH primero, PULL despues (docs/SYNC_ENGINE.md §3).
 * Es seguro llamarlo de nuevo tras un fallo: las operaciones son idempotentes
 * por `opId`, asi que un reintento tras un timeout no duplica nada.
 */
export async function runSyncOnce(options: SyncOptions): Promise<SyncOutcome> {
  const { db } = options;
  const outcome: SyncOutcome = {
    pushed: 0,
    pulled: 0,
    rejected: 0,
    conflicts: 0,
    unknownEntities: 0,
    superseded: 0,
    cursor: 0,
    error: null,
  };

  const state = await db.syncState.get('singleton');
  outcome.cursor = state?.lastPullSeq ?? 0;

  try {
    await rearmarOperacionesColgadas(options);
    const resultado = await pushPending(options);
    outcome.pushed = resultado.pushed;
    outcome.rejected = resultado.rejected;
    outcome.superseded = resultado.superseded;
    const pullResult = await pullChanges(options, outcome);
    outcome.cursor = pullResult;
    await db.syncState.update('singleton', { lastPushAt: new Date().toISOString(), lastError: null });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    outcome.error = message;
    logger.error('sync', 'SYNC_CYCLE_FAILED', 'Ciclo de sincronizacion fallido', {
      message,
    });
    await db.syncState.update('singleton', { lastError: message });
  }

  return outcome;
}

/**
 * Devuelve a `pending` las operaciones que quedaron en vuelo.
 *
 * Un `in_flight` solo existe mientras dura un push. Si al empezar un ciclo hay
 * alguna, es que el proceso anterior murio en mitad del envio: el sistema
 * operativo cerro la app, el movil se quedo sin bateria o se recargo la pagina.
 * Sin esto se quedaban ahi para siempre —`pushPending` solo mira `pending` y
 * `failed`—, asi que el dato estaba a salvo en local pero NO volvia a intentar
 * subir jamas, y el contador de pendientes se quedaba clavado.
 *
 * Reenviarlas es seguro aunque el envio anterior si llegara al servidor: el
 * `op_id` es el mismo y `sync_push` es idempotente.
 */
async function rearmarOperacionesColgadas(options: SyncOptions): Promise<number> {
  const { db } = options;
  const colgadas = await db.outbox.where('status').equals('in_flight').toArray();
  if (colgadas.length === 0) return 0;

  await db.outbox.bulkPut(colgadas.map((op) => ({ ...op, status: 'pending' as const })));
  logger.warn('sync', 'SYNC_OPS_REARMED', 'Operaciones en vuelo recuperadas tras un cierre brusco', {
    operaciones: colgadas.length,
  });
  return colgadas.length;
}

async function pushPending(
  options: SyncOptions,
): Promise<{ pushed: number; rejected: number; superseded: number }> {
  const { db, transport } = options;

  // Se ordena en memoria y no con `sortBy`: el criterio no es un solo campo,
  // sino la dependencia entre entidades (ver ORDEN_DE_ENVIO).
  const pending = ordenarParaEnvio(
    await db.outbox.where('status').anyOf('pending', 'failed').toArray(),
  );

  if (pending.length === 0) return { pushed: 0, rejected: 0, superseded: 0 };

  let pushed = 0;
  let rejected = 0;
  let superseded = 0;
  for (let i = 0; i < pending.length; i += PUSH_BATCH) {
    const batch = pending.slice(i, i + PUSH_BATCH).filter((op) => op.attempts < MAX_ATTEMPTS);
    if (batch.length === 0) continue;

    await db.outbox.bulkPut(batch.map((op) => ({ ...op, status: 'in_flight' as const })));

    let result;
    try {
      result = await transport.push(batch);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      await db.outbox.bulkPut(
        batch.map((op) => ({
          ...op,
          status: 'failed' as const,
          attempts: op.attempts + 1,
          lastError: message,
        })),
      );
      throw error;
    }

    const applied = new Set(result.applied);
    const rejectedById = new Map(result.rejected.map((r) => [r.opId, r.reason]));

    /*
     * Las que el servidor NO aplico porque tiene algo que gana (0026).
     *
     * Lo dice el, y ya no se deduce aqui. Antes se comparaba la `rev` enviada
     * con la devuelta, que funcionaba pero era una SEGUNDA cuenta de lo mismo:
     * dos formas de responder a «¿escribio?» acaban divergiendo sin que nada
     * avise, que es lo que este repositorio lleva documentado desde la Fase 1.
     */
    const superadas = new Set(result.superseded);

    await db.transaction('rw', db.outbox, async () => {
      for (const op of batch) {
        if (superadas.has(op.opId)) {
          // Se borra igual que una aplicada: reintentarla la volveria a
          // descartar para siempre, porque la version enviada no crece sola. Lo
          // que NO se hace es contarla como enviada y callarse.
          //
          // Converger es cosa de `applyRemoteRow`, que corre justo debajo con
          // la fila del servidor y decide con la politica de la entidad. Si la
          // version local es mas nueva por `updatedAt`, avisa de conflicto y el
          // usuario se entera.
          superseded += 1;
          logger.warn('sync', 'SYNC_PUSH_SUPERSEDED', 'El servidor no aplico un cambio local', {
            entity: op.entity,
            entityId: op.entityId,
          });
          await db.outbox.delete(op.opId);
        } else if (applied.has(op.opId)) {
          pushed += 1;
          await db.outbox.delete(op.opId);
        } else {
          const reason = rejectedById.get(op.opId) ?? 'sin respuesta del servidor';
          const permanente = esRechazoPermanente(reason);
          rejected += 1;
          await db.outbox.put({
            ...op,
            // Un rechazo definitivo no vuelve a la rueda: ni gasta los cinco
            // intentos ni cuenta como cambio pendiente. Se queda a la vista,
            // con su motivo, para que el usuario decida.
            status: permanente ? 'undeliverable' : 'failed',
            attempts: op.attempts + 1,
            lastError: reason,
          });
          logger.warn(
            'sync',
            permanente ? 'SYNC_OP_UNDELIVERABLE' : 'SYNC_OP_REJECTED',
            permanente
              ? 'Operacion que el servidor no va a aceptar nunca'
              : 'Operacion rechazada por el servidor',
            {
              entity: op.entity,
              entityId: op.entityId,
              reason,
            },
          );
        }
      }
    });

    // El servidor devuelve la fila resultante: se aplica para converger.
    for (const { entity, row } of result.rows) {
      await applyRemoteRow(options, entity, row);
    }
  }

  return { pushed, rejected, superseded };
}

async function pullChanges(options: SyncOptions, outcome: SyncOutcome): Promise<number> {
  const { db, transport } = options;
  const state = await db.syncState.get('singleton');
  let cursor = state?.lastPullSeq ?? 0;
  let hasMore = true;

  while (hasMore) {
    const page = await transport.pull(cursor, PULL_PAGE);
    for (const { entity, row } of page.rows) {
      const applied = await applyRemoteRow(options, entity, row);
      if (applied === 'conflict') outcome.conflicts += 1;
      if (applied === 'unknown') outcome.unknownEntities += 1;
      outcome.pulled += 1;
    }

    // El cursor TIENE que avanzar mientras el servidor diga que queda mas. Si
    // no avanza, la siguiente vuelta pide exactamente la misma pagina: bucle
    // infinito dentro de un `await`, con la aplicacion congelada, sin error y
    // sin nada en el registro. No es hipotetico —el fallo de paginacion de la
    // Fase 1 vivia en esta misma funcion SQL— y el coste de la guarda es una
    // comparacion por pagina.
    //
    // LA CONDICION MIRA `hasMore`, Y ANTES NO
    //
    // Miraba `rows.length === 0`, y eso convertia el final normal de la bajada
    // —cero filas nuevas, mismo cursor, `hasMore: false`— en un aviso. Salia en
    // cada ciclo de sincronizacion sin nada que bajar, o sea casi siempre: en
    // los 34 E2E aparecia decenas de veces. Un aviso que se dispara cuando todo
    // va bien deja de leerse, y este avisa justo de la familia de fallos de
    // R-17, que es la que no da ningun error por su cuenta.
    //
    // Ademas cubre mas que antes: con filas devueltas, el cursor quieto y
    // `hasMore` en true, la version anterior no cortaba —exigia cero filas— y
    // se quedaba reaplicando la misma pagina para siempre.
    if (page.cursor <= cursor && page.hasMore) {
      logger.warn('sync', 'SYNC_PULL_STALLED', 'La bajada no avanza: se corta el ciclo', {
        cursor,
        cursorDevuelto: page.cursor,
        filas: page.rows.length,
      });
      break;
    }

    cursor = page.cursor;
    hasMore = page.hasMore;
    await db.syncState.update('singleton', { lastPullSeq: cursor });
  }

  // Se marca tambien cuando no ha bajado ni una fila, y tambien si el ciclo se
  // corto por la guarda de arriba: lo que este campo afirma no es «tengo todo»
  // sino «he hablado con el servidor», que es justo lo que hace falta para
  // dejar de confundir «no hay fila» con «no la he mirado» (NEW5-002).
  await db.syncState.update('singleton', { lastPullAt: new Date().toISOString() });

  return cursor;
}

type ApplyResult = 'applied' | 'skipped' | 'conflict' | 'superseded' | 'unknown';

/**
 * Aplica una fila remota. NO encola nada en el outbox: el cambio ya esta en el
 * servidor. Los conflictos se resuelven con la politica por clase de entidad.
 */
export async function applyRemoteRow(
  options: SyncOptions,
  entity: EntityName,
  rawRow: Record<string, unknown>,
): Promise<ApplyResult> {
  const { db } = options;

  // Una entidad que este cliente no conoce.
  //
  // El tipo dice que `ENTITY_TO_TABLE` las tiene todas, pero eso es una promesa
  // sobre ESTA compilacion, no sobre lo que manda el servidor: el otro
  // dispositivo puede ir una version por delante y estar escribiendo filas de
  // una tabla que aqui todavia no existe. Sin esta guarda, `db.table(undefined)`
  // revienta, el ciclo entero se va por el `catch` de `runSyncOnce` y **el
  // cursor no avanza nunca mas**: la misma pagina falla en cada intento y este
  // dispositivo deja de recibir TODO, no solo esa entidad. Lo unico visible
  // seria un `lastError` repetido en Mas → Sincronizacion.
  //
  // El camino de subida ya se defendia de esto —`compararOps` manda las
  // entidades desconocidas al final «asi que un contrato mas nuevo que este
  // motor no rompe el lote»— y el de bajada no. Esa asimetria es el fallo.
  //
  // Se salta, y no se guarda en ningun sitio, porque **no hay donde**: no
  // existe la tabla local. Bloquear la sincronizacion entera para proteger una
  // fila que este cliente no puede almacenar es estrictamente peor. La fila
  // llega cuando el cliente se actualice, con el Plan B de R-17: cursor a 0 y
  // resincronizacion completa.
  const tableName = ENTITY_TO_TABLE[entity] as string | undefined;
  if (tableName === undefined) {
    logger.warn('sync', 'SYNC_UNKNOWN_ENTITY', 'Entidad desconocida en la bajada: se salta', {
      entity,
      id: typeof rawRow['id'] === 'string' ? rawRow['id'] : null,
    });
    return 'unknown';
  }

  const table = db.table(tableName);
  // Los sellos llegan del servidor en su propio formato; se canonizan antes de
  // comparar nada, o una fila identica pareceria distinta.
  const remote = normalizarSellos(
    keysToCamel(rawRow) as Record<string, unknown>,
  ) as unknown as SyncColumns;

  const local = (await table.get(remote.id)) as SyncColumns | undefined;
  if (!local) {
    await table.put(remote);
    return 'applied';
  }

  if (local.rev === remote.rev && local.updatedAt === remote.updatedAt) {
    // Misma version: solo se anota el sello del servidor.
    await table.put({ ...local, syncSeq: remote.syncSeq });
    return 'skipped';
  }

  const decision = resolveConflict(entity, local, remote);
  if (decision.winner === 'remote' || decision.winner === 'recompute') {
    await table.put(remote);
    if (decision.winner === 'recompute') {
      logger.info('sync', 'SYNC_DERIVED_RECOMPUTED', 'Entidad derivada tomada del servidor', {
        entity,
        id: remote.id,
      });
    }
    return 'applied';
  }

  // Gana la version local. Ahora bien, «gana» no significa «hay conflicto».
  //
  // El caso normal es este: se envia la revision N, el servidor la devuelve, y
  // para cuando vuelve el eco ya se ha escrito la N+1 en el mismo dispositivo
  // —al terminar una sesion eso pasa varias veces seguidas—. Eso no es un
  // conflicto: es la fila propia llegando tarde. Avisar de ello enseñaria al
  // usuario a ignorar los avisos de conflicto, que es peor que no tenerlos.
  //
  // Un conflicto de verdad es que la version descartada venga de OTRO
  // dispositivo, que es el unico caso en el que hay dos intenciones distintas.
  const mismoDispositivo = remote.originDeviceId === local.originDeviceId;

  if (mismoDispositivo) {
    logger.debug('sync', 'SYNC_OWN_ECHO_SUPERSEDED', 'Eco propio ya superado por una version mas nueva', {
      entity,
      id: remote.id,
      revLocal: local.rev,
      revRemoto: remote.rev,
    });
    return 'superseded';
  }

  options.onConflict?.(entity, remote.id, decision.reasonCodes.join(','));
  logger.warn('sync', 'SYNC_LOCAL_WINS', 'La version local prevalece sobre la de otro dispositivo', {
    entity,
    id: remote.id,
    reasonCodes: decision.reasonCodes,
  });
  return 'conflict';
}

export async function pendingOpsCount(db: CadenciaDb): Promise<number> {
  return db.outbox.where('status').anyOf('pending', 'failed', 'in_flight').count();
}

export async function listFailedOps(db: CadenciaDb): Promise<OutboxOp[]> {
  return db.outbox.where('status').equals('failed').toArray();
}

/**
 * Codigos de PostgreSQL que NO cambian por esperar.
 *
 * `sync_push` devuelve el motivo como `sqlstate || ' ' || sqlerrm` (migracion
 * 0026), asi que el codigo viaja delante y se puede clasificar sin adivinar
 * por el texto, que ademas viene en el idioma del servidor.
 *
 * Los cuatro comparten forma: la operacion describe un dato que la base no
 * puede aceptar tal cual. Reintentarla manana da exactamente el mismo error.
 *
 *   23503  clave ajena: el padre no existe. El caso real que trajo esto aqui:
 *          un movil intentando subir 118 filas de un plan cuya semana se habia
 *          borrado en duro del servidor —sin lapida, asi que el movil nunca se
 *          entero—. Cinco intentos cada una y a la cola para siempre.
 *   23502  columna obligatoria a nulo.
 *   23514  restriccion `check` incumplida.
 *   22P02  texto que no es del tipo que la columna espera.
 *
 * Lo que NO esta aqui, y a proposito: `23505` (clave unica), `40001` y `40P01`
 * (serializacion e interbloqueo), `42501` (RLS) y cualquier fallo de red. Los
 * tres ultimos son transitorios de manual, y una clave unica puede dejar de
 * chocar si la fila que estorba se borra. Ante la duda, se reintenta: aparcar
 * de mas pierde un dato del usuario, aparcar de menos solo gasta un intento.
 */
const CODIGOS_PERMANENTES = ['23503', '23502', '23514', '22P02'] as const;

/** La unica respuesta del servidor sin `sqlstate` que tampoco va a cambiar. */
const MOTIVO_ENTIDAD_NO_PERMITIDA = 'entidad no permitida';

/**
 * Si un rechazo del servidor es definitivo. Se mira el PRINCIPIO del motivo,
 * que es donde `sync_push` pone el codigo; buscarlo en cualquier posicion
 * clasificaria como permanente un mensaje que solo lo mencione.
 */
export function esRechazoPermanente(motivo: string | null): boolean {
  if (motivo === null) return false;
  if (motivo.startsWith(MOTIVO_ENTIDAD_NO_PERMITIDA)) return true;
  return CODIGOS_PERMANENTES.some((codigo) => motivo.startsWith(codigo));
}

export async function listUndeliverableOps(db: CadenciaDb): Promise<OutboxOp[]> {
  return db.outbox.where('status').equals('undeliverable').toArray();
}

/**
 * Reclasifica las `failed` que ya estaban aparcadas antes de que existiera
 * `undeliverable`.
 *
 * Sin esto el arreglo solo valdria para los rechazos FUTUROS, y el dispositivo
 * que ya esta en ese estado —118 operaciones imposibles contadas como
 * «cambios pendientes», y un boton de reintentar que las revive— seguiria
 * igual hasta que alguien borrase sus datos. Corre al arrancar, cuesta una
 * lectura del outbox y no toca nada si no hay nada que mover.
 */
export async function reclasificarRechazosPermanentes(db: CadenciaDb): Promise<number> {
  const permanentes = (await listFailedOps(db)).filter((op) => esRechazoPermanente(op.lastError));
  if (permanentes.length === 0) return 0;

  await db.outbox.bulkPut(permanentes.map((op) => ({ ...op, status: 'undeliverable' as const })));
  logger.warn(
    'sync',
    'SYNC_OPS_UNDELIVERABLE',
    'Operaciones que el servidor no va a aceptar nunca',
    { operaciones: permanentes.length },
  );
  return permanentes.length;
}

/**
 * Tira las operaciones imposibles. Es una decision del usuario y no del motor:
 * lo que se descarta es un cambio suyo que no llego a subir, y aunque no pueda
 * aplicarse nunca, esa frase la tiene que leer alguien antes de borrar nada.
 */
export async function discardUndeliverableOps(db: CadenciaDb): Promise<number> {
  const imposibles = await listUndeliverableOps(db);
  if (imposibles.length === 0) return 0;
  await db.outbox.bulkDelete(imposibles.map((op) => op.opId));
  logger.info('sync', 'SYNC_OPS_DISCARDED', 'Operaciones imposibles descartadas por el usuario', {
    operaciones: imposibles.length,
  });
  return imposibles.length;
}

/**
 * Devuelve a `pending` las operaciones agotadas. Sin esto, una operacion que
 * alcanza MAX_ATTEMPTS queda excluida de todo envio futuro para siempre: el
 * dato existe en el movil y no llega nunca al servidor.
 */
export async function retryFailedOps(db: CadenciaDb): Promise<number> {
  const fallidas = await listFailedOps(db);
  if (fallidas.length === 0) return 0;
  await db.outbox.bulkPut(
    fallidas.map((op) => ({ ...op, status: 'pending' as const, attempts: 0 })),
  );
  logger.info('sync', 'SYNC_RETRY_REQUESTED', 'Reintento manual de operaciones fallidas', {
    operaciones: fallidas.length,
  });
  return fallidas.length;
}

/**
 * Devolver el cursor de bajada a cero: el Plan B de R-17.
 *
 * POR QUE HACE FALTA UNA PALANCA Y NO BASTA CON ARREGLAR EL SERVIDOR
 *
 * La migracion 0015 impidio que el cursor saltara por encima de la cola de una
 * entidad truncada. Lo que **no** puede hacer es devolver las filas que un
 * cliente ya se salto: su cursor esta por delante de ellas y la bajada solo
 * pide `sync_seq > cursor`, asi que esas filas no se piden nunca mas. Arreglar
 * la funcion protege a quien no haya sincronizado todavia; a quien ya perdio
 * algo no le devuelve nada.
 *
 * Paso el 20/08/2026 en el dispositivo real: el gimnasio se dio de alta ese
 * dia —con la funcion vieja, siete dias antes de la 0015— y de las 1810 cargas
 * seleccionables llegaron **500**, exactamente una pagina. Veinticuatro de las
 * treinta y cinco maquinas se quedaron sin una sola carga, y el motor de cargas
 * recomendaba lo unico que le quedaba: 7,5 kg para una prensa de 45 grados. No
 * dio ningun error, porque no hay ninguno que dar.
 *
 * `docs/RISKS.md` R-17 ya decia «cursor a 0 y resincronizacion completa» como
 * Plan B, y hasta hoy **no habia forma de hacerlo desde la aplicacion**: la
 * unica salida era borrar los datos del dispositivo, que ademas cierra la
 * sesion y tira lo que estuviera sin subir.
 *
 * LO QUE ESTO NO HACE, Y HAY QUE DECIRLO
 *
 * Trae lo que falte; **no borra lo que aqui sobre**. Una fila eliminada del
 * servidor sin lapida no se entera nadie: la bajada solo añade y actualiza. Y
 * no toca el outbox: lo pendiente de subir sigue pendiente.
 */
export async function resetPullCursor(db: CadenciaDb): Promise<number> {
  const estado = await db.syncState.get('singleton');
  if (estado === undefined) return 0;

  const anterior = estado.lastPullSeq;
  await db.syncState.put({ ...estado, lastPullSeq: 0 });
  logger.warn('sync', 'SYNC_FULL_RESYNC_REQUESTED', 'Cursor de bajada a cero', {
    cursorAnterior: anterior,
  });
  return anterior;
}

/**
 * Cuantas filas hay en la base local, sumando todas las entidades.
 *
 * Es la medida que hace visible lo invisible: la unica señal de que faltaban
 * 1310 cargas era el numero, porque ninguna pantalla enseña un hueco. Se usa
 * antes y despues de una resincronizacion completa para poder decir cuantas
 * filas trajo, en vez de un «listo» que no demuestra nada.
 */
export async function contarFilasLocales(db: CadenciaDb): Promise<number> {
  let total = 0;
  for (const tabla of Object.values(ENTITY_TO_TABLE)) {
    total += await db.table(tabla).count();
  }
  return total;
}
